import { useState, useEffect, useRef } from 'react';

export default function SearchableSelect({ options, value, onChange, placeholder, className, onCreateOption }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options || []);
  const [creating, setCreating] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setFilteredOptions(
      (options || []).filter(opt => opt.toLowerCase().includes(search.toLowerCase()))
    );
  }, [search, options]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option);
    setSearch('');
    setIsOpen(false);
  };

  const handleCreate = async () => {
    if (!onCreateOption || !search.trim() || creating) return;
    setCreating(true);
    try {
      await onCreateOption(search.trim());
      onChange(search.trim());
      setSearch('');
      setIsOpen(false);
    } finally {
      setCreating(false);
    }
  };

  const trimmedSearch = search.trim();
  const hasExactMatch = filteredOptions.some(o => o.toLowerCase() === trimmedSearch.toLowerCase());
  const showCreateOption = onCreateOption && trimmedSearch && !hasExactMatch;

  return (
    <div ref={wrapperRef} className={`relative ${className || ''}`}>
      <input
        type="text"
        value={isOpen ? search : (value || '')}
        onChange={(e) => setSearch(e.target.value)}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder || 'Selecionar...'}
        className="w-full px-3 py-2 bg-[var(--surface-3)] border border-[var(--border)] text-[var(--text-base)] placeholder-[var(--text-muted)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
      />
      {isOpen && (filteredOptions.length > 0 || showCreateOption || !onCreateOption) && (
        <div className="absolute z-50 w-full bg-[var(--surface-2)] border border-[var(--border)] rounded-lg mt-1 max-h-48 overflow-y-auto shadow-xl">
          {filteredOptions.length === 0 && !showCreateOption && (
            <div className="px-3 py-2 text-[var(--text-muted)] text-sm">Nenhuma opção encontrada</div>
          )}
          {filteredOptions.map((option, idx) => (
            <div
              key={idx}
              className="px-3 py-2 cursor-pointer text-sm text-[var(--text-base)] hover:bg-[var(--surface-4)]"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(option); }}
            >
              {option}
            </div>
          ))}
          {showCreateOption && (
            <div
              className="px-3 py-2 cursor-pointer text-sm text-blue-400 hover:bg-[var(--surface-4)] border-t border-[var(--border)] flex items-center gap-2"
              onMouseDown={(e) => { e.preventDefault(); handleCreate(); }}
            >
              <span className="font-bold">+</span>
              {creating ? 'Criando...' : `Criar "${trimmedSearch}"`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
